// src/App.tsx
import { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AppLayout from '../layouts/AppLayout/AppLayout';
import RequireAuth from '../auth/RequireAuth/RequireAuth';
import SessionModeBridge from '../auth/SessionModeBridge/SessionModeBridge';
import { appRoutes } from '../routes';
import PageLoader from '../components/PageLoader/PageLoader';
import ErrorBoundary from '../components/ErrorBoundary/ErrorBoundary';
import styles from './App.module.scss';

// Full-page auth routes — no layout, no guard.
const authPaths = new Set(['/login', '/signup']);

// Public routes that use the layout but DON'T require login.
// Everything not listed here (and not an auth path) is gated.
const publicPaths = new Set([
	'/',
	'/simulate', // Pass 1 — the public funnel: explore before signing up
	'/simulate/:view', // the Plan and Lab rooms share the funnel posture
	'/impact-lab', // anon explores real-life scenarios before signing up
	'/impact-lab/:scenarioId',
	'/impact-lab/market-replay', // shares Impact Lab's public funnel posture
	'/impact-lab/market-replay/:replayId',
	'/quizzes', // discovery quiz hub — anon-accessible
	'/quizzes/:quizId', // discovery quiz runners — anon-accessible
	'/legal/terms',
	'/legal/disclaimer',
	'/legal/privacy',
	'*', // 404
]);

const App = () => {
	const authRoutes = appRoutes.filter((r) => authPaths.has(r.path));
	const publicRoutes = appRoutes.filter(
		(r) => !authPaths.has(r.path) && publicPaths.has(r.path),
	);
	const protectedRoutes = appRoutes.filter(
		(r) => !authPaths.has(r.path) && !publicPaths.has(r.path),
	);

	return (
		<Router
			future={{
				v7_startTransition: true,
				v7_relativeSplatPath: true,
			}}>
			<SessionModeBridge />
			<div className={styles.appContainer}>
				{/* App-shell boundary: last resort for a crash in the chrome
				    (layout, nav) itself. Renders the full-page fallback. */}
				<ErrorBoundary scope='app-shell'>
					<Suspense fallback={<PageLoader />}>
						<Routes>
							{/* Full-page auth routes (no layout) */}
							{authRoutes.map(({ path, element }, idx) => (
								<Route
									key={`auth-${path}-${idx}`}
									path={path}
									element={element}
								/>
							))}

							{/* Public routes under the layout, no guard.
							    The route boundary sits INSIDE the layout so a
							    page crash keeps the sidebar/header and offers a
							    local recovery. */}
							<Route element={<AppLayout />}>
								{publicRoutes.map(({ path, element }, idx) => (
									<Route
										key={`public-${path}-${idx}`}
										path={path}
										element={
											<ErrorBoundary scope='route'>
												{element}
											</ErrorBoundary>
										}
									/>
								))}
							</Route>

							{/* Protected routes: guard → layout → route boundary */}
							<Route element={<RequireAuth />}>
								<Route element={<AppLayout />}>
									{protectedRoutes.map(
										({ path, element }, idx) => (
											<Route
												key={`protected-${path}-${idx}`}
												path={path}
												element={
													<ErrorBoundary scope='route'>
														{element}
													</ErrorBoundary>
												}
											/>
										),
									)}
								</Route>
							</Route>
						</Routes>
					</Suspense>
				</ErrorBoundary>
			</div>
		</Router>
	);
};

export default App;
